import React, { useState } from 'react';
import './CustomersTable.css';

const CustomersTable = ({
  customers,
  loading,
  onViewDetails,
  currentPage,
  totalPages,
  totalCustomers,
  itemsPerPage,
  onPageChange
}) => {
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'Premium':
        return '#F7B801';
      case 'Regular':
        return '#fe0000';
      case 'New':
        return '#2D6A4F';
      default:
        return '#6C757D';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleActionClick = (customerId, e) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === customerId ? null : customerId);
  };

  const handleActionSelect = (action, customer, e) => {
    e.stopPropagation();
    setActionMenuOpen(null);
    
    switch (action) {
      case 'view':
        onViewDetails(customer);
        break;
      case 'history':
        console.log('View order history for:', customer.id);
        break;
      case 'message':
        console.log('Send message to:', customer.id);
        break;
      case 'block':
        console.log('Block customer:', customer.id);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="customers-table-card">
        <div className="loading-skeleton">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="customers-table-card">
        <div className="empty-state">
          <span className="empty-icon">👥</span>
          <p className="empty-text">No customers yet</p>
          <p className="empty-subtext">Customers will appear here when they place orders</p>
        </div>
      </div>
    );
  }

  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(startIndex + customers.length - 1, totalCustomers);

  return (
    <div className="customers-table-card">
      {/* Desktop Table View */}
      <div className="table-container desktop-view">
        <table className="customers-table">
          <thead>
            <tr>
              <th>CUSTOMER</th>
              <th>CONTACT</th>
              <th>ORDERS</th>
              <th>TOTAL SPENT</th>
              <th>LAST ORDER</th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => (
              <tr key={customer.id} onClick={() => onViewDetails(customer)}>
                <td className="customer-cell">
                  <div className="customer-info">
                    {customer.profilePhoto ? (
                      <img
                        src={customer.profilePhoto}
                        alt={customer.name}
                        className="customer-avatar"
                      />
                    ) : (
                      <div
                        className="customer-avatar initials"
                        style={{ backgroundColor: getBadgeColor(customer.badge) + '20' }}
                      >
                        <span style={{ color: getBadgeColor(customer.badge) }}>
                          {getInitials(customer.name)}
                        </span>
                      </div>
                    )}
                    <div className="customer-details">
                      <div className="customer-name">{customer.name}</div>
                      <div
                        className="customer-badge"
                        style={{ backgroundColor: getBadgeColor(customer.badge) }}
                      >
                        {customer.badge}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="contact-cell">
                  <div className="contact-info">
                    <a href={`tel:${customer.phone}`} className="contact-phone" onClick={(e) => e.stopPropagation()}>
                      📞 {customer.phone}
                    </a>
                    <div className="contact-email">{customer.email}</div>
                  </div>
                </td>
                <td className="orders-cell">
                  <div className="orders-number">{customer.totalOrders}</div>
                  <div className="orders-label">orders</div>
                </td>
                <td className="spent-cell">
                  <div className="spent-amount">PKR {customer.totalSpent.toLocaleString()}</div>
                  <div className="spent-average">Avg: PKR {customer.averageOrder.toLocaleString()}</div>
                </td>
                <td className="last-order-cell">
                  <div className="last-order-date">{formatDate(customer.lastOrderDate)}</div>
                  <div className="last-order-time">{customer.lastOrderTime}</div>
                  <div className="last-order-days">{customer.daysAgo} days ago</div>
                </td>
                <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                  <div className="actions-menu-wrapper">
                    <button
                      className="actions-menu-btn"
                      onClick={(e) => handleActionClick(customer.id, e)}
                    >
                      ⋮
                    </button>
                    {actionMenuOpen === customer.id && (
                      <>
                        <div
                          className="actions-menu-overlay"
                          onClick={() => setActionMenuOpen(null)}
                        />
                        <div className="actions-menu">
                          <button
                            className="actions-menu-item"
                            onClick={(e) => handleActionSelect('view', customer, e)}
                          >
                            View Details
                          </button>
                          <button
                            className="actions-menu-item"
                            onClick={(e) => handleActionSelect('history', customer, e)}
                          >
                            Order History
                          </button>
                          <button
                            className="actions-menu-item"
                            onClick={(e) => handleActionSelect('message', customer, e)}
                          >
                            Send Message
                          </button>
                          <button
                            className="actions-menu-item danger"
                            onClick={(e) => handleActionSelect('block', customer, e)}
                          >
                            Block Customer
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-view">
        {customers.map(customer => (
          <div key={customer.id} className="customer-card-mobile" onClick={() => onViewDetails(customer)}>
            <div className="mobile-card-header">
              <div className="customer-info-mobile">
                {customer.profilePhoto ? (
                  <img
                    src={customer.profilePhoto}
                    alt={customer.name}
                    className="customer-avatar-mobile"
                  />
                ) : (
                  <div
                    className="customer-avatar-mobile initials"
                    style={{ backgroundColor: getBadgeColor(customer.badge) + '20' }}
                  >
                    <span style={{ color: getBadgeColor(customer.badge) }}>
                      {getInitials(customer.name)}
                    </span>
                  </div>
                )}
                <div className="customer-details-mobile">
                  <div className="customer-name-mobile">{customer.name}</div>
                  <div
                    className="customer-badge-mobile"
                    style={{ backgroundColor: getBadgeColor(customer.badge) }}
                  >
                    {customer.badge}
                  </div>
                </div>
              </div>
              <div className="actions-menu-wrapper-mobile" onClick={(e) => e.stopPropagation()}>
                <button
                  className="actions-menu-btn-mobile"
                  onClick={(e) => handleActionClick(customer.id, e)}
                >
                  ⋮
                </button>
                {actionMenuOpen === customer.id && (
                  <>
                    <div
                      className="actions-menu-overlay"
                      onClick={() => setActionMenuOpen(null)}
                    />
                    <div className="actions-menu-mobile">
                      <button
                        className="actions-menu-item"
                        onClick={(e) => handleActionSelect('view', customer, e)}
                      >
                        View Details
                      </button>
                      <button
                        className="actions-menu-item"
                        onClick={(e) => handleActionSelect('history', customer, e)}
                      >
                        Order History
                      </button>
                      <button
                        className="actions-menu-item"
                        onClick={(e) => handleActionSelect('message', customer, e)}
                      >
                        Send Message
                      </button>
                      <button
                        className="actions-menu-item danger"
                        onClick={(e) => handleActionSelect('block', customer, e)}
                      >
                        Block Customer
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mobile-card-body">
              <div className="mobile-info-row">
                <div className="mobile-info-item">
                  <div className="mobile-info-label">Contact</div>
                  <a href={`tel:${customer.phone}`} className="mobile-info-value" onClick={(e) => e.stopPropagation()}>
                    📞 {customer.phone}
                  </a>
                  <div className="mobile-info-value-small">{customer.email}</div>
                </div>
              </div>

              <div className="mobile-stats-row">
                <div className="mobile-stat-item">
                  <div className="mobile-stat-number">{customer.totalOrders}</div>
                  <div className="mobile-stat-label">Orders</div>
                </div>
                <div className="mobile-stat-item">
                  <div className="mobile-stat-number">PKR {customer.totalSpent.toLocaleString()}</div>
                  <div className="mobile-stat-label">Total Spent</div>
                </div>
                <div className="mobile-stat-item">
                  <div className="mobile-stat-number">{formatDate(customer.lastOrderDate).split(',')[0]}</div>
                  <div className="mobile-stat-label">Last Order</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="table-pagination">
        <div className="pagination-info">
          Showing {startIndex}-{endIndex} of {totalCustomers} customers
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            &lt; Previous
          </button>
          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1;
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 2 && page <= currentPage + 2)
            ) {
              return (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => onPageChange(page)}
                >
                  {page}
                </button>
              );
            } else if (page === currentPage - 3 || page === currentPage + 3) {
              return <span key={page} className="pagination-ellipsis">...</span>;
            }
            return null;
          })}
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomersTable;

